<template>
  <div class="multilanguage-text">
    <template v-if="isAllMode">
      <div class="multilanguage-text__row" v-for="item in text" :key="item.langCode">
        <div class="multilanguage-text__flag multilanguage-text__flag--static">
          <v-img :src="getLanguageIcon(item.langCode)" width="16" />
        </div>

        <slot :value="item.value"> {{ item.value }} </slot>
      </div>
    </template>

    <div v-else class="multilanguage-text__single">
      <div class="multilanguage-text__flag" @click="handleTextChange">
        <v-img :src="activeTextIcon" width="16" />
      </div>

      <slot :value="activeTextValue"> {{ activeTextValue }} </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import type { MultilanguageValue } from '@/core/utils/languages';
import { getLanguageIcon } from '@/core/utils/languages';

const props = defineProps<{
  text: MultilanguageValue[];
  mode?: 'single' | 'all';
}>();

defineSlots<{
  default: (props: { value: string }) => void;
}>();

const activeTextIndex = ref(0);

const activeLang = toRef(() => props.text[activeTextIndex.value]);

const activeTextIcon = computed(() => (activeLang.value ? getLanguageIcon(activeLang.value.langCode) : undefined));
const activeTextValue = computed(() => activeLang.value?.value);

const handleTextChange = () => {
  if (activeTextIndex.value === props.text.length - 1) {
    activeTextIndex.value = 0;
  } else {
    activeTextIndex.value++;
  }
};

const isAllMode = computed(() => props.mode === 'all');
</script>

<style scoped lang="scss">
.multilanguage-text {
  display: flex;
  flex-direction: column;
  gap: 8px;

  word-break: break-word;
}
.multilanguage-text__row {
  display: flex;
  align-items: center;
}

.multilanguage-text__single {
  display: flex;
  align-items: baseline;
}

.multilanguage-text__flag {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;
  margin-right: 8px;

  background-color: $primary-light-color;
  border-radius: 6px;

  cursor: pointer;

  @include hover {
    background-color: $primary-tonal-color;
  }
}

.multilanguage-text__flag--static {
  cursor: default;

  &:hover {
    background-color: $primary-light-color;
  }
}
</style>
